import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Calendar, dateFnsLocalizer } from 'react-big-calendar';
import format from 'date-fns/format';
import parse from 'date-fns/parse';
import startOfWeek from 'date-fns/startOfWeek';
import getDay from 'date-fns/getDay';
import enUS from 'date-fns/locale/en-US';
import { addMonths, endOfMonth, startOfMonth, subMonths } from 'date-fns';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { CalendarDays, Plus, Check, X, Trash2, ClipboardList } from 'lucide-react';
import ScheduleEventModal from '@/components/admin/ScheduleEventModal';
import SchedulePendingApprovalModal from '@/components/admin/SchedulePendingApprovalModal';
import {
  STATUS_STYLES,
  STATUS_LABELS,
  EMPLOYEE_EVENT_TYPES,
  SERVICE_EVENT_TYPES,
} from '@/types/schedule';
import { formatDateMdY, formatDateTimeMdY } from '@/utils/dateFormat';
import 'react-big-calendar/lib/css/react-big-calendar.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const locales = { 'en-US': enUS };
const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
});

const calendarFormats = {
  dateFormat: 'MM/dd/yyyy',
  dayFormat: 'MM/dd',
  weekdayFormat: 'EEE',
  monthHeaderFormat: 'MM/yyyy',
  dayHeaderFormat: 'MM/dd/yyyy',
  dayRangeHeaderFormat: ({ start, end }) => `${format(start, 'MM/dd/yyyy')} – ${format(end, 'MM/dd/yyyy')}`,
  agendaDateFormat: 'MM/dd/yyyy',
  agendaHeaderFormat: ({ start, end }) => `${format(start, 'MM/dd/yyyy')} – ${format(end, 'MM/dd/yyyy')}`,
};

const EVENT_TYPE_LABELS = Object.fromEntries([
  ...EMPLOYEE_EVENT_TYPES.map((t) => [t.value, t.label]),
  ...SERVICE_EVENT_TYPES.map((t) => [t.value, t.label]),
]);

const SCHEDULE_MIN_HOUR = 10;
const SCHEDULE_MAX_HOUR = 19; // 7:00 PM

function buildScheduleBoundary(hour, minute = 0) {
  const d = new Date();
  d.setHours(hour, minute, 0, 0);
  return d;
}

const scheduleMinTime = buildScheduleBoundary(SCHEDULE_MIN_HOUR);
const scheduleMaxTime = buildScheduleBoundary(SCHEDULE_MAX_HOUR);
const scheduleScrollToTime = buildScheduleBoundary(SCHEDULE_MIN_HOUR);

function eventStyleGetter(event) {
  const status = event.resource?.status || 'approved';
  const colors = {
    pending_approval: { backgroundColor: '#b45309', borderColor: '#f59e0b' },
    approved: { backgroundColor: '#1d4ed8', borderColor: '#3b82f6' },
    rejected: { backgroundColor: '#7f1d1d', borderColor: '#ef4444' },
    completed: { backgroundColor: '#065f46', borderColor: '#10b981' },
  };
  const style = colors[status] || colors.approved;
  return {
    className: 'schedule-cal-event',
    style: { ...style, color: '#fff', borderRadius: 4, border: 'none' },
  };
}

/** Wraps each hour cell in week/day views for isolated hover */
function ScheduleTimeSlotWrapper({ value, children }) {
  const isSunday = value?.getDay?.() === 0;
  return (
    <div className={`schedule-hour-slot${isSunday ? ' schedule-slot-sunday' : ''}`}>
      {children}
    </div>
  );
}

function StatusBadge({ status }) {
  return (
    <span className={`inline-flex px-2 py-0.5 text-[11px] font-['Manrope'] border ${STATUS_STYLES[status] || STATUS_STYLES.approved}`}>
      {STATUS_LABELS[status] || status}
    </span>
  );
}

export default function AdminSchedulePage() {
  const { token } = useAuth();
  const [activeTab, setActiveTab] = useState('employee');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [calendarView, setCalendarView] = useState(() => (
    typeof window !== 'undefined' && window.innerWidth < 1024 ? 'agenda' : 'week'
  ));
  const [events, setEvents] = useState([]);
  const [pending, setPending] = useState([]);
  const [outreachRequests, setOutreachRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pendingLoading, setPendingLoading] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [requestModalOpen, setRequestModalOpen] = useState(false);
  const [requestActionLoading, setRequestActionLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editEvent, setEditEvent] = useState(null);
  const [slotStart, setSlotStart] = useState(null);

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const range = useMemo(() => {
    const start = startOfMonth(subMonths(currentDate, 1));
    const end = endOfMonth(addMonths(currentDate, 1));
    return { start: start.toISOString(), end: end.toISOString() };
  }, [currentDate]);

  const fetchPendingRequests = useCallback(async () => {
    if (!token) return;
    setPendingLoading(true);
    try {
      const pendingRes = await axios.get(`${BACKEND_URL}/api/admin/schedule/pending`, { headers });
      setPending(pendingRes.data.pending || []);
      setOutreachRequests(pendingRes.data.outreach_requests || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load pending approvals');
    } finally {
      setPendingLoading(false);
    }
  }, [token, headers]);

  const fetchData = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const requests = [
        axios.get(`${BACKEND_URL}/api/admin/schedule/${activeTab}`, {
          headers,
          params: { start: range.start, end: range.end, include_pending: false },
        }),
        axios.get(`${BACKEND_URL}/api/admin/schedule/pending`, { headers }),
      ];
      const [eventsRes, pendingRes] = await Promise.all(requests);
      setEvents(eventsRes.data.events || []);
      setPending(pendingRes.data.pending || []);
      setOutreachRequests(pendingRes.data.outreach_requests || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load schedule');
    } finally {
      setLoading(false);
    }
  }, [token, headers, activeTab, range.start, range.end]);

  useEffect(() => {
    if (activeTab === 'pending') {
      fetchPendingRequests();
      return;
    }
    fetchData();
  }, [activeTab, fetchData, fetchPendingRequests]);

  const calendarEvents = useMemo(
    () => events.map((ev) => ({
      id: ev.id,
      title: `${ev.title}${ev.status === 'pending_approval' ? ' (Pending)' : ''}`,
      start: new Date(ev.start_at),
      end: new Date(ev.end_at),
      resource: ev,
    })),
    [events],
  );

  const eventDays = useMemo(() => {
    const days = new Set();
    events.forEach((ev) => {
      days.add(format(new Date(ev.start_at), 'yyyy-MM-dd'));
    });
    return days;
  }, [events]);

  const calendarComponents = useMemo(
    () => ({
      timeSlotWrapper: ScheduleTimeSlotWrapper,
    }),
    [],
  );

  const openCreate = (start) => {
    if (start && start.getDay() === 0) {
      toast.error('Events cannot be scheduled on Sundays');
      return;
    }
    if (start) {
      const hour = start.getHours();
      const minute = start.getMinutes();
      if (hour < SCHEDULE_MIN_HOUR || hour > SCHEDULE_MAX_HOUR || (hour === SCHEDULE_MAX_HOUR && minute > 0)) {
        const adjusted = new Date(start);
        adjusted.setHours(SCHEDULE_MIN_HOUR, 0, 0, 0);
        start = adjusted;
      }
    }
    setEditEvent(null);
    setSlotStart(start || new Date());
    setModalOpen(true);
  };

  const handleSelectEvent = (calEvent) => {
    setSelectedEvent(calEvent.resource);
  };

  const handleApproveOutreachRequest = async (request) => {
    setRequestActionLoading(true);
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/schedule/approve/${request.request_id}`,
        {},
        { headers },
      );
      const approvedEvent = res.data?.event;
      const calendarType = approvedEvent?.calendar_type || request.calendar_type;
      const calendarLabel = calendarType === 'service' ? 'Service' : 'Employee';

      if (approvedEvent?.start_at) {
        setCurrentDate(new Date(approvedEvent.start_at));
      }

      await fetchPendingRequests();

      if (calendarType === 'employee' || calendarType === 'service') {
        setActiveTab(calendarType);
      }
      if (approvedEvent) {
        setSelectedEvent(approvedEvent);
      }

      toast.success(`Request approved — added to ${calendarLabel} calendar`);
      setRequestModalOpen(false);
      setSelectedRequest(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to approve request');
    } finally {
      setRequestActionLoading(false);
    }
  };

  const handleRejectOutreachRequest = async (request, notes = '') => {
    setRequestActionLoading(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/schedule/reject/${request.request_id}`,
        { notes: notes || undefined },
        { headers },
      );
      toast.success('Request rejected');
      setRequestModalOpen(false);
      setSelectedRequest(null);
      fetchPendingRequests();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reject request');
    } finally {
      setRequestActionLoading(false);
    }
  };

  const openRequestDetail = (request) => {
    setSelectedRequest(request);
    setRequestModalOpen(true);
  };

  const handleDelete = async (event) => {
    if (!window.confirm('Delete this scheduled event?')) return;
    try {
      await axios.delete(
        `${BACKEND_URL}/api/admin/schedule/${activeTab}/${event.id}`,
        { headers },
      );
      toast.success('Event deleted');
      setSelectedEvent(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete event');
    }
  };

  const handleMarkCompleted = async (event) => {
    try {
      await axios.put(
        `${BACKEND_URL}/api/admin/schedule/${activeTab}/${event.id}`,
        { status: 'completed' },
        { headers },
      );
      toast.success('Marked as completed');
      setSelectedEvent(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update event');
    }
  };

  return (
    <div className="p-4 sm:p-6 max-w-[1500px] mx-auto" data-testid="admin-schedule-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-['Unbounded'] font-bold text-white flex items-center gap-2">
            <CalendarDays className="w-6 h-6 text-blue-400" />
            Schedule
          </h1>
          <p className="text-sm font-['Manrope'] text-white/40 mt-1">
            Employee and service calendars — Mon–Sat, 10:00 AM – 7:00 PM
          </p>
        </div>
        {activeTab !== 'pending' && (
          <button
            type="button"
            onClick={() => openCreate(new Date())}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-500 text-white text-sm font-['Manrope'] font-medium"
          >
            <Plus className="w-4 h-4" />
            New {activeTab === 'employee' ? 'Employee' : 'Service'} Event
          </button>
        )}
      </div>

      <div className="flex gap-0 mb-6 border-b border-white/10 overflow-x-auto [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {[
          { id: 'employee', label: 'Employee Schedule' },
          { id: 'service', label: 'Service Schedule' },
          {
            id: 'pending',
            label: 'Pending Approvals',
            count: outreachRequests.length,
          },
        ].map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => { setActiveTab(tab.id); setSelectedEvent(null); }}
            className={`shrink-0 px-4 py-3 min-h-11 text-sm font-['Manrope'] font-medium border-b-2 transition-colors inline-flex items-center gap-2 ${
              activeTab === tab.id
                ? 'border-blue-400 text-blue-400'
                : 'border-transparent text-white/50 hover:text-white/80'
            }`}
          >
            {tab.id === 'pending' && <ClipboardList className="w-4 h-4" />}
            {tab.label}
            {tab.count > 0 && (
              <span className="px-1.5 py-0.5 text-[10px] bg-amber-500/20 text-amber-300 border border-amber-500/30">
                {tab.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {activeTab === 'pending' ? (
        <div className="bg-white/[0.03] border border-white/10 overflow-hidden">
          {pendingLoading ? (
            <p className="text-white/40 font-['Manrope'] text-sm p-12 text-center">Loading pending requests…</p>
          ) : outreachRequests.length === 0 ? (
            <div className="p-12 text-center">
              <ClipboardList className="w-10 h-10 text-white/20 mx-auto mb-3" />
              <p className="text-white/50 font-['Manrope'] text-sm">No pending approval requests</p>
              <p className="text-white/30 font-['Manrope'] text-xs mt-1">
                Outreach agent scheduling requests will appear here for review.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <p className="lg:hidden px-4 py-2 text-[11px] font-['Manrope'] text-white/40">Swipe sideways for all columns. Month grid is easier on desktop.</p>
              <table className="w-full min-w-[960px]">
                <thead className="bg-white/5 border-b border-white/10">
                  <tr>
                    {[
                      'Event Type',
                      'Customer / Lead',
                      'Vehicle',
                      'Preferred Date & Time',
                      'Duration',
                      'Notes',
                      'Requested By',
                      'Actions',
                    ].map((col) => (
                      <th
                        key={col}
                        className="px-4 py-3 text-left text-xs font-['Manrope'] font-semibold text-white/70 uppercase tracking-wider"
                      >
                        {col}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/10">
                  {outreachRequests.map((request) => (
                    <tr
                      key={request.request_id}
                      onClick={() => openRequestDetail(request)}
                      className="hover:bg-white/5 transition-colors cursor-pointer"
                    >
                      <td className="px-4 py-3 text-sm font-['Manrope'] text-white">
                        {request.event_type_label || request.event_type}
                      </td>
                      <td className="px-4 py-3 text-sm font-['Manrope'] text-white/80">
                        {request.customer_name}
                      </td>
                      <td className="px-4 py-3 text-sm font-['Manrope'] text-white/60">
                        {request.vehicle_info}
                      </td>
                      <td className="px-4 py-3 text-sm font-['Manrope'] text-white/80 whitespace-nowrap">
                        {formatDateTimeMdY(request.preferred_start_at, '—')}
                      </td>
                      <td className="px-4 py-3 text-sm font-['Manrope'] text-white/60">
                        {request.duration_label || `${request.duration_minutes} min`}
                      </td>
                      <td className="px-4 py-3 text-sm font-['Manrope'] text-white/50 max-w-[180px] truncate">
                        {request.notes || '—'}
                      </td>
                      <td className="px-4 py-3 text-sm font-['Manrope'] text-white/50">
                        {request.source_label || 'Outreach Agent'}
                      </td>
                      <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                        <div className="flex gap-2">
                          <button
                            type="button"
                            onClick={() => handleApproveOutreachRequest(request)}
                            disabled={requestActionLoading}
                            className="inline-flex items-center gap-1 px-2.5 py-1.5 bg-emerald-600/80 hover:bg-emerald-500 text-white text-xs font-['Manrope'] disabled:opacity-50"
                          >
                            <Check className="w-3.5 h-3.5" /> Approve
                          </button>
                          <button
                            type="button"
                            onClick={() => handleRejectOutreachRequest(request)}
                            disabled={requestActionLoading}
                            className="inline-flex items-center gap-1 px-2.5 py-1.5 bg-red-600/40 hover:bg-red-500/60 text-white text-xs font-['Manrope'] disabled:opacity-50"
                          >
                            <X className="w-3.5 h-3.5" /> Reject
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ) : (
      <div className="grid grid-cols-1 xl:grid-cols-[1fr_320px] gap-6">
        <div className="bg-white/[0.03] border border-white/10 p-4 schedule-calendar-dark min-h-[620px]">
          {loading ? (
            <p className="text-white/40 font-['Manrope'] text-sm p-8">Loading calendar…</p>
          ) : (
            <Calendar
              localizer={localizer}
              events={calendarEvents}
              startAccessor="start"
              endAccessor="end"
              formats={calendarFormats}
              style={{ height: 580 }}
              views={['month', 'week', 'day', 'agenda']}
              view={calendarView}
              onView={setCalendarView}
              date={currentDate}
              onNavigate={setCurrentDate}
              min={scheduleMinTime}
              max={scheduleMaxTime}
              scrollToTime={scheduleScrollToTime}
              selectable
              onSelectSlot={({ start }) => openCreate(start)}
              onSelectEvent={handleSelectEvent}
              components={calendarComponents}
              eventPropGetter={eventStyleGetter}
              dayPropGetter={(date) => {
                const isSunday = date.getDay() === 0;
                const dayKey = format(date, 'yyyy-MM-dd');
                const isToday = dayKey === format(new Date(), 'yyyy-MM-dd');
                const hasEvents = eventDays.has(dayKey);
                const classes = [
                  isSunday
                    ? 'rbc-off-range-bg opacity-40 pointer-events-none schedule-day-sunday'
                    : 'schedule-day-cell',
                  isToday ? 'schedule-day-today' : '',
                  hasEvents ? 'schedule-day-has-events' : 'schedule-day-empty',
                ].filter(Boolean).join(' ');
                return { className: classes };
              }}
            />
          )}
        </div>

        <div className="bg-white/[0.03] border border-white/10 p-4 h-fit">
          <h3 className="text-sm font-['Unbounded'] text-white mb-3">Event Details</h3>
          {!selectedEvent ? (
            <p className="text-xs font-['Manrope'] text-white/40">
              Select an event on the calendar or create a new one. Outreach requests are reviewed under Pending Approvals.
            </p>
          ) : (
            <div className="space-y-3">
              <div>
                <p className="text-base font-['Manrope'] text-white font-medium">{selectedEvent.title}</p>
                <div className="mt-1"><StatusBadge status={selectedEvent.status} /></div>
              </div>
              <p className="text-xs font-['Manrope'] text-white/50">
                {EVENT_TYPE_LABELS[selectedEvent.event_type] || selectedEvent.event_type}
              </p>
              <p className="text-xs font-['Manrope'] text-white/60">
                {formatDateMdY(selectedEvent.start_at)}
                <br />
                {format(new Date(selectedEvent.start_at), 'h:mm a')}
                {' – '}
                {format(new Date(selectedEvent.end_at), 'h:mm a')}
                {' '}
                ({selectedEvent.duration_minutes} min)
              </p>
              {selectedEvent.description && (
                <p className="text-xs font-['Manrope'] text-white/50 border-t border-white/10 pt-2">
                  {selectedEvent.description}
                </p>
              )}
              {selectedEvent.links && (
                <div className="text-xs font-['Manrope'] text-white/40 space-y-1 border-t border-white/10 pt-2">
                  {selectedEvent.links.customer_name && <p>Customer: {selectedEvent.links.customer_name}</p>}
                  {selectedEvent.links.customer_phone && <p>Phone: {selectedEvent.links.customer_phone}</p>}
                  {selectedEvent.links.vehicle_info && <p>Vehicle: {selectedEvent.links.vehicle_info}</p>}
                  {selectedEvent.links.deal_id && <p>Deal: {selectedEvent.links.deal_id}</p>}
                  {selectedEvent.links.lead_id && (
                    <p>Lead: {selectedEvent.links.lead_id} ({selectedEvent.links.lead_type})</p>
                  )}
                  {selectedEvent.links.service_request_id && (
                    <p>Service Request: {selectedEvent.links.service_request_id}</p>
                  )}
                </div>
              )}
              <div className="flex flex-wrap gap-2 pt-2">
                {selectedEvent.status === 'approved' && (
                  <button
                    type="button"
                    onClick={() => handleMarkCompleted(selectedEvent)}
                    className="px-3 py-1.5 text-xs font-['Manrope'] bg-blue-600/80 hover:bg-blue-500 text-white"
                  >
                    Mark Completed
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => { setEditEvent(selectedEvent); setModalOpen(true); }}
                  className="px-3 py-1.5 text-xs font-['Manrope'] bg-white/10 hover:bg-white/15 text-white"
                >
                  Edit
                </button>
                <button
                  type="button"
                  onClick={() => handleDelete(selectedEvent)}
                  className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-['Manrope'] bg-red-600/30 hover:bg-red-500/40 text-red-200"
                >
                  <Trash2 className="w-3.5 h-3.5" /> Delete
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
      )}

      <SchedulePendingApprovalModal
        open={requestModalOpen}
        request={selectedRequest}
        onClose={() => { setRequestModalOpen(false); setSelectedRequest(null); }}
        onApprove={handleApproveOutreachRequest}
        onReject={handleRejectOutreachRequest}
        processing={requestActionLoading}
      />

      <ScheduleEventModal
        open={modalOpen}
        onClose={() => { setModalOpen(false); setEditEvent(null); }}
        onSaved={fetchData}
        calendarType={activeTab}
        token={token}
        editEvent={editEvent}
        initialStart={slotStart}
      />

      <style>{`
        .schedule-calendar-dark .rbc-calendar { color: #e5e5e5; font-family: 'Manrope', sans-serif; }
        .schedule-calendar-dark .rbc-toolbar button {
          color: #a3a3a3; border: 1px solid rgba(255,255,255,0.1); background: rgba(255,255,255,0.05);
        }
        .schedule-calendar-dark .rbc-toolbar button.rbc-active,
        .schedule-calendar-dark .rbc-toolbar button:hover {
          background: rgba(59,130,246,0.25); color: #fff;
        }
        .schedule-calendar-dark .rbc-header,
        .schedule-calendar-dark .rbc-time-header-content,
        .schedule-calendar-dark .rbc-time-content,
        .schedule-calendar-dark .rbc-month-view,
        .schedule-calendar-dark .rbc-day-bg { border-color: rgba(255,255,255,0.08); }
        .schedule-calendar-dark .rbc-off-range-bg { background: rgba(255,255,255,0.02); }

        /* Today highlight — brighter so it reads clearly on dark background */
        .schedule-calendar-dark .rbc-today,
        .schedule-calendar-dark .schedule-day-today {
          background: rgba(147, 197, 253, 0.18) !important;
          box-shadow: inset 0 0 0 1px rgba(147, 197, 253, 0.45);
        }
        .schedule-calendar-dark .rbc-month-view .rbc-now .rbc-button-link {
          color: #93c5fd;
          font-weight: 700;
        }

        .schedule-calendar-dark .rbc-event { font-size: 12px; }

        /* Month: empty day = full square hover; days with events = per-event hover only */
        .schedule-calendar-dark .rbc-month-view .rbc-row-segment:empty {
          pointer-events: none;
        }
        .schedule-calendar-dark .rbc-month-view .schedule-day-cell {
          transition: transform 0.3s ease-out, box-shadow 0.3s ease-out, background 0.25s ease-out;
        }
        .schedule-calendar-dark .rbc-month-view .schedule-day-empty:hover {
          transform: translateY(-4px);
          box-shadow: 0 10px 28px rgba(0, 0, 0, 0.45);
          background: rgba(255, 255, 255, 0.07) !important;
          z-index: 2;
        }
        .schedule-calendar-dark .rbc-month-view .schedule-day-has-events:hover {
          transform: none;
          box-shadow: none;
        }
        .schedule-calendar-dark .rbc-date-cell {
          pointer-events: none;
        }
        .schedule-calendar-dark .schedule-cal-event {
          transition: transform 0.3s ease-out, box-shadow 0.3s ease-out !important;
        }
        .schedule-calendar-dark .rbc-month-view .schedule-cal-event:hover,
        .schedule-calendar-dark .rbc-time-view .schedule-cal-event:hover {
          transform: translateY(-4px) !important;
          box-shadow: 0 8px 22px rgba(0, 0, 0, 0.45) !important;
          z-index: 12 !important;
        }

        /* Week/Day: horizontal line between each hour — brighter for visibility */
        .schedule-calendar-dark .rbc-time-view .rbc-time-slot {
          border-top: 1px solid rgba(255, 255, 255, 0.22) !important;
          box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.06);
        }
        .schedule-calendar-dark .rbc-time-view .rbc-timeslot-group {
          border-bottom: none !important;
        }
        .schedule-calendar-dark .rbc-time-view .rbc-time-content > .rbc-time-gutter .rbc-timeslot-group:first-child .rbc-time-slot:first-child,
        .schedule-calendar-dark .rbc-time-view .rbc-day-slot .rbc-timeslot-group:first-child .rbc-time-slot:first-child {
          border-top: none !important;
        }
        .schedule-calendar-dark .rbc-time-view .rbc-day-slot {
          background: transparent !important;
        }
        .schedule-calendar-dark .rbc-time-view .rbc-day-slot:hover {
          background: transparent !important;
          transform: none !important;
          box-shadow: none !important;
        }
        .schedule-calendar-dark .rbc-time-view .rbc-events-container .schedule-cal-event + .schedule-cal-event {
          border-top: 1px solid rgba(255, 255, 255, 0.2);
          margin-top: 2px;
          padding-top: 2px;
        }

        /* Week/Day: hover individual hour boxes only (not whole column) */
        .schedule-calendar-dark .schedule-hour-slot {
          height: 100%;
          min-height: 100%;
          transition: transform 0.3s ease-out, box-shadow 0.3s ease-out, background 0.25s ease-out;
          position: relative;
          z-index: 1;
        }
        .schedule-calendar-dark .schedule-hour-slot:hover {
          transform: translateY(-3px);
          box-shadow: 0 8px 20px rgba(0, 0, 0, 0.38);
          background: rgba(59, 130, 246, 0.14);
          z-index: 4;
        }
        .schedule-calendar-dark .schedule-slot-sunday {
          pointer-events: none;
          opacity: 0.35;
        }

        /* Agenda rows */
        .schedule-calendar-dark .rbc-agenda-table tbody tr {
          transition: transform 0.3s ease-out, box-shadow 0.3s ease-out, background 0.25s ease-out;
        }
        .schedule-calendar-dark .rbc-agenda-table tbody tr:hover {
          transform: translateY(-3px);
          box-shadow: 0 8px 20px rgba(0, 0, 0, 0.35);
          background: rgba(255, 255, 255, 0.06);
        }
      `}</style>
    </div>
  );
}